import { CSSProperties, ReactNode } from 'react'
import { cn } from 'utils/cn'

export interface BadgeProps {
    variant?: 'primary' | 'secondary' | 'soft'
    type?: 'accent' | 'default' | 'success' | 'warning' | 'danger'
    size?: 'lg' | 'md' | 'sm'
    label?: string
    className?: string
    prefix?: ReactNode
    suffix?: ReactNode
    /** Set false to drop the background fill and render just the icon/text in the type color. */
    filled?: boolean
    style?: CSSProperties
}

const sizeClasses: Record<string, string> = {
    lg: 'h-8 min-w-8 rounded-2xl p-1.5 text-sm leading-[1.43]',
    md: 'h-7 min-w-7 rounded-3xl p-1.5 text-xs leading-[1.34]',
    sm: 'h-[18px] min-w-[18px] rounded-xl p-1 text-[10px] leading-[1.34]',
}

export type BadgeType = NonNullable<BadgeProps['type']>

/**
 * The solid dot that stands in for a badge where a pill does not fit — a menu row offering the status the
 * badge displays, for instance.
 *
 * It is NOT `bgClasses.primary`: that table is a PILL fill, and `default` there is `bg-default`, a surface
 * tone chosen to sit quietly behind text. At 8px with no text on it that reads as nothing at all, so the
 * neutral dot takes the muted foreground instead. Everything else matches the pill exactly.
 *
 * Exported so a caller never hand-writes the mapping: a second copy drifts from the badge beside it, which
 * is the whole failure this replaces.
 */
export const badgeDotClasses: Record<BadgeType, string> = {
    accent: 'bg-accent',
    default: 'bg-foreground-muted',
    success: 'bg-success',
    warning: 'bg-warning',
    danger: 'bg-danger',
}

const bgClasses: Record<string, Record<string, string>> = {
    primary: {
        accent: 'bg-accent',
        default: 'bg-default',
        success: 'bg-success',
        warning: 'bg-warning',
        danger: 'bg-danger',
    },
    secondary: {
        accent: 'bg-default',
        default: 'bg-default',
        success: 'bg-default',
        warning: 'bg-default',
        danger: 'bg-default',
    },
    soft: {
        accent: 'bg-accent-soft',
        default: 'bg-default',
        success: 'bg-success-soft',
        warning: 'bg-warning-soft',
        danger: 'bg-danger-soft',
    },
}

const fgClasses: Record<string, Record<string, string>> = {
    primary: {
        accent: 'text-accent-foreground',
        default: 'text-default-foreground',
        success: 'text-success-foreground',
        warning: 'text-warning-foreground',
        danger: 'text-danger-foreground',
    },
    secondary: {
        accent: 'text-accent',
        default: 'text-default-foreground',
        success: 'text-success',
        warning: 'text-warning',
        danger: 'text-danger',
    },
    soft: {
        accent: 'text-accent',
        default: 'text-default-foreground',
        success: 'text-success',
        warning: 'text-warning',
        danger: 'text-danger',
    },
}

export const Badge = ({ variant = 'primary', type = 'default', size = 'md', label, prefix, suffix, filled = true, style, className }: BadgeProps) => (
    <span
        className={cn(
            'box-border inline-flex items-center justify-center gap-0.5 whitespace-nowrap font-medium',
            sizeClasses[size],
            filled ? bgClasses[variant][type] : 'bg-transparent',
            fgClasses[variant][type],
            className
        )}
        style={style}
    >
        {prefix && <span className="flex shrink-0 items-center">{prefix}</span>}
        {label && <span>{label}</span>}
        {suffix && <span className="flex shrink-0 items-center">{suffix}</span>}
    </span>
)
